import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowsRotate } from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "../../../i18n/LanguageContext";

/**
 * Unified Refresh Button component.
 *
 * Variants:
 *  - "default" : icon + text on desktop, icon-only on mobile (≤768px)
 *  - "ghost"   : subtle/low-emphasis, same responsive collapse
 *  - "header"  : always icon-only, for use on colored section headers
 *
 * @param {function} onClick       – Required. The refresh handler.
 * @param {boolean}  loading       – Shows spin animation & disables button.
 * @param {string}   variant       – "default" | "ghost" | "header"
 * @param {string}   size          – "sm" | "md"
 * @param {string}   label         – Button text (default "Refresh").
 * @param {string}   loadingLabel  – Text shown while loading (default "Refreshing...").
 * @param {boolean}  disabled      – Additional disabled control.
 * @param {string}   title         – Tooltip override.
 * @param {string}   className     – Escape-hatch for extra styling.
 */
const RefreshButton = ({
  onClick,
  loading = false,
  variant = "default",
  size = "md",
  label,
  loadingLabel,
  disabled = false,
  title,
  className = "",
}) => {
  const { t } = useTranslation();
  const resolvedLabel = label ?? t("common.refresh");
  const resolvedLoadingLabel = loadingLabel ?? t("common.refreshing");
  const isAlwaysIconOnly = variant === "header";

  const classes = [
    "refresh-btn-base",
    `refresh-btn-${variant}`,
    size === "sm" ? "refresh-btn-sm" : "",
    loading ? "refresh-btn-spinning" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <button
      className={classes}
      onClick={onClick}
      disabled={disabled || loading}
      title={title || resolvedLabel}
      aria-label={loading ? resolvedLoadingLabel : resolvedLabel}
      type="button"
    >
      <FontAwesomeIcon icon={faArrowsRotate} />
      {!isAlwaysIconOnly && (
        <span className="refresh-btn-label">
          {loading ? resolvedLoadingLabel : resolvedLabel}
        </span>
      )}
    </button>
  );
};

export default RefreshButton;
